/**
 * What blocking a sender actually hits.
 *
 * This is the single most destructive decision in the inbox: a business domain
 * block marks EVERY contact on that domain as Not Qualified, so getting
 * "consumer provider" wrong by one entry would blacklist a whole shared
 * provider (e.g. every gmail contact in the world). Hence it lives in its own
 * pure module with tests, separate from the hook that does the writing.
 *
 * `functions/blockInboxSender.js` mirrors this function because the server is
 * the one that performs the block; the provider list both sides read from is
 * held in sync by scripts/check-enums.js, so the confirmation dialog can never
 * promise something different from what happens.
 */

import { CONSUMER_EMAIL_PROVIDERS } from '../../../shared/enums';
import { extractEmail } from './emailParsing';

/**
 * @param {string} fromHeader raw From value (e.g. `Jane <jane@acme.com>`)
 * @returns {{ emailAddress: string, domain: string, isConsumer: boolean,
 *             blockEntry: string, blockType: 'domain'|'email' } | null}
 *   null when no address can be read out of the header.
 */
export const getBlockTarget = (fromHeader) => {
  const emailAddress = extractEmail(fromHeader);
  if (!emailAddress) return null;

  const atIdx = emailAddress.indexOf('@');
  const domain = atIdx !== -1 ? emailAddress.substring(atIdx).toLowerCase() : '';
  if (!domain) return null;

  const isConsumer = CONSUMER_EMAIL_PROVIDERS.includes(domain);
  return {
    emailAddress,
    domain,
    isConsumer,
    // Consumer providers are blocked per address, never per domain.
    blockEntry: isConsumer ? emailAddress : domain,
    blockType: isConsumer ? 'email' : 'domain',
  };
};

export default getBlockTarget;
